import React, { useState, useEffect } from 'react';
import { Upload, Search, FileText, User, Users, AlertCircle, CheckCircle, Loader2, Home, Calendar, UserCircle2, CreditCard, X, Sparkles, File, Hash } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- IMPORT YOUR LOGO HERE ---
import logo from '../../assests/images/logo1.png'; 

const QUOTES = [
  "Empowering the digital frontier with GLOCYBS and the unmatched precision of BIQUA.",
  "Where innovation meets security, GLOCYBS leads the way.",
  "Building the future of secure data extraction.",
  "Precision in every pixel, security in every byte.",
  "Turning complex data into actionable insights.",
  "BIQUA: The gold standard for digital document processing.",
  "Secure. Fast. Reliable. The GLOCYBS promise.",
  "Accelerating your digital transformation journey.",
  "Data privacy is not an option, it is a GLOCYBS guarantee.",
  "Redefining boundaries with quantum-ready security."
];

const GlocybsPDFSearch = () => {
  const [pdfFile, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('name');
  const [searchResults, setSearchResults] = useState([]);
  const [uploadedData, setUploadedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quoteIndex, setQuoteIndex] = useState(0);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';

  // Rotate Quotes
  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteIndex((prev) => (prev + 1) % QUOTES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setError(null);
    if (file && file.type === 'application/pdf') {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size exceeds 10MB limit');
        return;
      }
      setFile(file);
    } else {
      setError('Please select a valid PDF file');
    }
  };

  const uploadPDF = async () => {
    if (!pdfFile) {
      setError('Please select a PDF file first');
      return;
    }
    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append('pdf', pdfFile);

    try {
      const response = await fetch(`${API_BASE}/pdf/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        setUploadedData(data.data);
        setFile(null);
        setError(null);
      } else {
        setError(data.error || 'Upload failed.');
      }
    } catch (error) {
      console.error("Upload Error:", error);
      setError('Network error. Is the backend running?');
    } finally {
      setUploading(false);
    }
  };

  const searchPDF = async (term = searchQuery) => {
    if (!term.trim()) {
      setError('Please enter a search query');
      return;
    }
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_BASE}/pdf/search?field=${searchField}&query=${encodeURIComponent(term)}`
      );
      const data = await response.json();
      if (response.ok) {
        setSearchResults(data.data || []);
        setError(null);
      } else {
        setError(data.error || 'Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Search Error:", error);
      setError('Network error during search.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pt-24">
      
      {/* --- HERO HEADER --- */}
      <div className="bg-white shadow-sm border-b border-gray-200 mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            
            {/* LOGO */}
            <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center shadow-md border border-gray-100 p-2 transform hover:scale-105 transition-transform duration-300">
               {logo ? (
                 <img src={logo} alt="Glocybs Logo" className="w-full h-full object-contain" />
               ) : (
                 <FileText className="w-8 h-8 text-blue-600" />
               )}
            </div>

            {/* BRAND NAME */}
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-none">
                <span className="text-black">GLO</span>
                <span className="text-cyan-500">CYBS</span>
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 font-medium tracking-wide mt-1">
                ADVANCED DOCUMENT INTELLIGENCE
              </p>
            </div>
          </div>

          {/* QUOTE CAROUSEL */}
          <div className="flex-1 max-w-2xl w-full min-h-[64px] flex items-center justify-center md:justify-end text-center md:text-right relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={quoteIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="text-xs sm:text-sm font-semibold text-gray-600 italic px-6 border-r-4 md:border-r-0 md:border-l-4 border-cyan-500 bg-gray-50 py-3 rounded-lg shadow-sm"
              >
                "{QUOTES[quoteIndex]}"
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-12 space-y-8">
        
        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 text-red-700 mb-6"
            >
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span className="flex-1 font-medium text-sm">{error}</span>
              <button onClick={() => setError(null)}><X className="w-5 h-5" /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* --- MAIN GRID LAYOUT --- */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* LEFT COLUMN: UPLOAD */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white rounded-2xl shadow-xl shadow-cyan-100/50 p-6 border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-black to-cyan-500"></div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-6">
                <Upload className="w-5 h-5 text-cyan-600" />
                Upload Document
              </h2>
              
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-cyan-400 hover:bg-cyan-50/30 transition-all cursor-pointer group">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="pdf-upload"
                />
                <label htmlFor="pdf-upload" className="cursor-pointer block">
                  <div className="w-14 h-14 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:bg-cyan-100 group-hover:text-cyan-600 transition-all">
                    <FileText className="w-7 h-7" />
                  </div>
                  <p className="text-sm font-bold text-gray-900 truncate px-2">
                    {pdfFile ? pdfFile.name : 'Select PDF File'}
                  </p>
                  <p className="text-xs text-gray-500 mt-2 font-medium">Max 10MB • OCR Enabled</p>
                </label>
                {pdfFile && (
                  <button onClick={clearFile} className="mt-4 text-xs font-bold text-red-600 hover:text-red-800 bg-red-50 px-4 py-1.5 rounded-full transition-colors">
                    Remove
                  </button>
                )}
              </div>

              <button
                onClick={uploadPDF}
                disabled={!pdfFile || uploading}
                className="w-full mt-6 bg-black hover:bg-gray-900 text-white py-3.5 rounded-lg font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm uppercase tracking-wider"
              >
                {uploading ? <Loader2 className="animate-spin w-4 h-4" /> : <Sparkles className="w-4 h-4 text-cyan-400" />}
                {uploading ? 'Processing...' : 'Upload & Analyze'}
              </button>

              <AnimatePresence>
                {uploadedData && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg space-y-2"
                  >
                    <div className="flex items-center gap-3 text-green-800 text-sm">
                      <div className="bg-green-100 p-2 rounded-full">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-bold text-green-900">Success!</p>
                        <p className="text-xs mt-0.5">{uploadedData.total_records} records extracted</p>
                      </div>
                    </div>
                    {uploadedData.part_number && (
                      <div className="text-xs bg-white p-2 rounded border border-green-100">
                        <span className="font-semibold text-gray-600">Part Number:</span>{' '}
                        <span className="font-mono text-cyan-700">{uploadedData.part_number}</span>
                      </div>
                    )}
                    {uploadedData.pdf_filename && (
                      <div className="text-xs bg-white p-2 rounded border border-green-100">
                        <span className="font-semibold text-gray-600">File:</span>{' '}
                        <span className="text-gray-800">{uploadedData.pdf_filename}</span>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* RIGHT COLUMN: SEARCH & RESULTS */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* SEARCH BAR */}
            <div className="bg-white rounded-2xl shadow-lg p-2 sm:p-3 border border-gray-200 flex flex-col sm:flex-row gap-3 relative z-20">
              <div className="sm:w-48 flex-shrink-0">
                <select
                  value={searchField}
                  onChange={(e) => setSearchField(e.target.value)}
                  className="w-full h-full bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-cyan-500 focus:border-cyan-500 block p-3 font-medium"
                >
                  <option value="name">Name</option>
                  <option value="epic_number">EPIC No.</option>
                  <option value="relative_name">Relative</option>
                  <option value="house_number">House No.</option>
                  <option value="part_number">Part Number (भाग संख्या)</option>
                  <option value="pdf_filename">PDF Filename</option>
                </select>
              </div>
              
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchPDF()}
                  placeholder="Type to search database..."
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-cyan-500 focus:border-cyan-500 block p-3 pl-11 font-medium placeholder-gray-400"
                />
                <Search className="absolute left-3.5 top-3.5 w-5 h-5 text-gray-400" />
              </div>

              <button
                onClick={() => searchPDF()}
                disabled={loading}
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3 rounded-lg font-bold transition-all shadow-md hover:shadow-lg disabled:opacity-70 flex items-center justify-center gap-2 uppercase text-sm tracking-wide"
              >
                {loading ? <Loader2 className="animate-spin w-4 h-4" /> : "Search"}
              </button>
            </div>

            {/* RESULTS LIST */}
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
                  <Loader2 className="w-12 h-12 text-cyan-500 animate-spin mx-auto" />
                  <p className="text-gray-500 font-medium mt-4">Scanning records...</p>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {searchResults.map((record, index) => (
                    <motion.div
                      key={record.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-xl shadow-sm hover:shadow-xl border border-gray-200 overflow-hidden group transition-all duration-300"
                    >
                      <div className="p-5 sm:p-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-6 border-b border-gray-100 gap-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shadow-inner ${
                              record.gender === 'Female' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600'
                            }`}>
                              {record.gender === 'Female' ? 'F' : 'M'}
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 leading-tight">
                                {record.name}
                              </h3>
                              <p className="text-xs text-gray-500 font-mono mt-1 bg-gray-100 inline-block px-2 py-0.5 rounded">
                                SERIAL: #{record.serial_number || 'N/A'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="bg-black text-white px-4 py-2 rounded-lg text-xs font-mono font-bold tracking-widest shadow-md">
                            {record.epic_number || 'NO EPIC ID'}
                          </div>
                        </div>

                        {/* NEW: Part Number and PDF Filename Display */}
                        <div className="mb-4 flex flex-wrap gap-2">
                          {record.part_number && (
                            <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 px-3 py-1.5 rounded-lg">
                              <Hash className="w-3 h-3 text-purple-600" />
                              <span className="text-xs font-bold text-purple-900">
                                Part: {record.part_number}
                              </span>
                            </div>
                          )}
                          {record.pdf_filename && (
                            <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-lg">
                              <File className="w-3 h-3 text-indigo-600" />
                              <span className="text-xs font-semibold text-indigo-900 truncate max-w-[200px]">
                                {record.pdf_filename}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 group-hover:border-cyan-200 group-hover:bg-cyan-50/30 transition-colors">
                            <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider flex items-center gap-1">
                              <Users className="w-3 h-3" /> Relative
                            </span>
                            <span className="font-bold text-gray-800 break-words">{record.relative_name}</span>
                          </div>
                          
                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 group-hover:border-purple-200 group-hover:bg-purple-50/30 transition-colors">
                            <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider flex items-center gap-1">
                              <Home className="w-3 h-3" /> House No
                            </span>
                            <span className="font-bold text-gray-800">{record.house_number}</span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 group-hover:border-green-200 group-hover:bg-green-50/30 transition-colors">
                            <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> Age
                            </span>
                            <span className="font-bold text-gray-800">{record.age} Years</span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 group-hover:border-orange-200 group-hover:bg-orange-50/30 transition-colors">
                            <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider flex items-center gap-1">
                              <UserCircle2 className="w-3 h-3" /> Gender
                            </span>
                            <span className="font-bold text-gray-800">{record.gender}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-300">
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-5">
                    <Search className="w-10 h-10 text-gray-300" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">No records found</h3>
                  <p className="text-sm text-gray-500 mt-2 max-w-xs mx-auto">
                    Try checking your spelling or selecting a different search field above.
                  </p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default GlocybsPDFSearch;